import type { MarkerLayerRect } from "./types";

export interface Point2 {
  x: number;
  y: number;
}

/** Target-pixel corners in tracker/homography order: top-left, top-right, bottom-right, bottom-left. */
export type CornerQuad = readonly [Point2, Point2, Point2, Point2];

/**
 * A layer's normalized rect (`MarkerLayerRect` — width is the single reference unit for both
 * axes, see its own doc comment) turned into 4 corner points in **target-pixel** space, the same
 * space `transferQuad`'s full-marker-rect corners already live in (`@visorxr/tracker-planar`).
 * Transferring these through the tracker's own homography (via that package's `applyToPoint`,
 * once per corner) is what places a layer on the tracked marker — this function only computes
 * where the rect sits *before* that transfer, entirely independent of tracking.
 */
export function layerRectCorners(rect: MarkerLayerRect, targetSize: { width: number; height: number }): CornerQuad {
  const unit = targetSize.width;
  const x = rect.x * unit;
  const y = rect.y * unit;
  const width = rect.width * unit;
  const height = rect.height * unit;

  const unrotated: CornerQuad = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];

  const rotationDeg = rect.rotationDeg ?? 0;
  if (rotationDeg === 0) return unrotated;

  const center: Point2 = { x: x + width / 2, y: y + height / 2 };
  return unrotated.map((corner) => rotateAround(corner, center, rotationDeg)) as unknown as CornerQuad;
}

/**
 * Visually clockwise rotation in a y-down (screen) coordinate system. This is the same formula as
 * mathematical counter-clockwise rotation in a y-up system — flipping the y-axis flips which
 * direction "positive angle" turns, so the standard CCW rotation matrix reads as CW on screen.
 */
/**
 * The validator accepts any finite `rotationDeg` on purpose — the angle wraps, so 400 and 40 are
 * the same placement. Converting a very large finite value straight to radians overflows to
 * Infinity, and the four corners come back `{x: NaN, y: NaN}` on their way into renderer buffers,
 * so the wrap has to happen before the trigonometry rather than being left implicit in it.
 */
export function radiansFromDegrees(degrees: number): number {
  return ((degrees % 360) * Math.PI) / 180;
}

function rotateAround(point: Point2, center: Point2, degrees: number): Point2 {
  const radians = radiansFromDegrees(degrees);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}
